import { useState, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { Account } from 'shared/types/account';
import { Option } from 'components/commercetools-ui/atoms/dropdown';
import Select, { SelectProps } from 'components/commercetools-ui/atoms/dropdown/option-dropdown';
import Input, { InputProps } from 'components/commercetools-ui/atoms/input';
import { useFormat } from 'helpers/hooks/useFormat';
import useValidate from 'helpers/hooks/useValidate';
import { useAccount } from 'frontastic';
import AccountForm from '../../../account-atoms/account-form';
import useDiscardForm from '../../../hooks/useDiscardForm';

type inputNameType = 'firstName' | 'lastName';
type inputEmailType = 'email';

interface EmailProps {
  email: string;
  password: string;
}

const PersonalInfoForm = () => {
  const { account, update, login, logout } = useAccount();
  const { discardForm } = useDiscardForm();
  const [data, setData] = useState<Account>(account as Account);
  const [emailData, setEmailData] = useState<EmailProps>({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSubmitErrorMsg, setEmailSubmitErrorMsg] = useState('');
  const [personalInfoSubmitErrorMsg, setPersonalInfoSubmitErrorMsg] = useState('');

  const { validateEmail, validateMin2Chars, validateDOB } = useValidate();

  const { formatMessage } = useFormat({ name: 'common' });
  const { formatMessage: formatErrorMessage } = useFormat({ name: 'error' });
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const router = useRouter();
  const invalidNameErrorMessage = formatErrorMessage({
    id: 'name',
    defaultMessage: 'Name has to be at least two characters.',
  });
  const invalidDOBErrorMessage = formatErrorMessage({
    id: 'dob.age.limit',
    defaultMessage: 'The chosen date is not within the supported range.',
  });
  const invalidEmailErrorMessage = formatErrorMessage({
    id: 'email',
    defaultMessage: 'Please ensure the email address format is correct.',
  });
  const handleLogout = () => {
    logout().then(() => router.push('/login'));
  };

  const disableSubmit = useMemo(() => {
    return !(validateEmail(data?.email)
      ? validateMin2Chars(data?.firstName || '') &&
        validateMin2Chars(data?.lastName || '') &&
        validateDOB(data?.birthday || '')
      : false);
  }, [data]);

  // Input element change handler
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  // Select element change handler
  const selectHandleChage = (option: Option) => {
    setData({ ...data, title: option.value as string });
  };

  const handleSubmit = () => {
    setLoading(true);
    setEmailSubmitErrorMsg('');
    setPersonalInfoSubmitErrorMsg('');
    update(data)
      .then(() => setLoading(false))
      .then(() => {
        localStorage.setItem('personalInfoFormAction', 'profile-update');
        discardForm();
      })
      .catch(() => {
        setLoading(false);
        setPersonalInfoSubmitErrorMsg(
          formatAccountMessage({
            id: 'profile.update.error',
            defaultMessage: 'Something went wrong. Please try again later',
          }),
        );
      });
  };

  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 18);
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 100);

  const personalDetails: Array<InputProps & SelectProps> = [
    {
      label: formatMessage({ id: 'title', defaultMessage: 'Title' }),
      name: 'title',
      required: false,
      options: [
        { name: 'Mr', value: 'Mr' },
        { name: 'Mrs', value: 'Mrs' },
        { name: 'Ms', value: 'Ms' },
        { name: 'Miss', value: 'Miss' },
        { name: 'Dr', value: 'Dr' },
      ],
    },
    {
      label: formatMessage({ id: 'firstName', defaultMessage: 'First Name' }),
      name: 'firstName',
      maxLength: 21,
      errorMessage: invalidNameErrorMessage,
      validation: validateMin2Chars,
      required: true,
    },
    {
      label: formatMessage({ id: 'lastName', defaultMessage: 'Last Name' }),
      name: 'lastName',
      maxLength: 31,
      errorMessage: invalidNameErrorMessage,
      validation: validateMin2Chars,
      required: true,
    },
    {
      label: formatMessage({ id: 'birthday', defaultMessage: 'Date of Birth' }),
      name: 'birthday',
      type: 'date',
      min: new Date(minDate).toISOString().split('T')[0],
      max: new Date(maxDate).toISOString().split('T')[0],
      errorMessage: invalidDOBErrorMessage,
      validation: validateDOB,
      required: false,
    },
  ];

  const emailField: Array<InputProps> = [
    {
      label: formatMessage({ id: 'email', defaultMessage: 'Email' }),
      name: 'email',
      errorMessage: invalidEmailErrorMessage,
      validation(value) {
        return validateEmail(value);
      },
      required: true,
    },
    {
      label: formatAccountMessage({ id: 'password.current', defaultMessage: 'Current password' }),
      name: 'password',
      type: 'password',
      errorMessage: 'This field is required.',
      validation(value) {
        return value.length > 0;
      },
      required: true,
    },
  ];

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailData({ ...emailData, [e.target.name]: e.target.value });
  };
  const validatePassword = async (password: string) => {
    const oldAccountEmail = account?.email || '';
    const response = await login(oldAccountEmail, password, false);
    if (response.accountId) return true;
    return false;
  };

  const disableEmailSubmit = useMemo(() => {
    return !(validateEmail(emailData?.email) ? emailData.password.length > 0 : false);
  }, [emailData]);

  const handleEmailSubmit = () => {
    const currentEmail = data.email;
    const newData = { ...data, email: emailData.email };
    setData((prevState) => ({ ...prevState, email: emailData.email }));
    setEmailLoading(true);
    setEmailSubmitErrorMsg('');
    const isValid = async () => {
      const isValid = await validatePassword(emailData.password);
      if (!isValid) {
        setEmailLoading(false);
        setEmailSubmitErrorMsg(
          formatAccountMessage({ id: 'invalid.password', defaultMessage: 'The old password you entered is invalid.' }),
        );
        setData((prevState) => ({ ...prevState, email: currentEmail }));
        return;
      }
      update(newData)
        .then(() => {
          setLoading(false);
        })
        .then(() => {
          //localStorage.setItem('personalInfoFormAction', 'email-update');
          handleLogout();
          router.push('/login');
        })
        .catch((error) => {
          setEmailLoading(false);

          const errorMsg = error.message;
          setEmailLoading(false);

          if (errorMsg.includes('existing customer')) {
            setEmailSubmitErrorMsg(
              formatAccountMessage({
                id: 'duplicate.email',
                defaultMessage: 'Email address is already in use, please enter a new email address.',
              }),
            );
          } else {
            setEmailSubmitErrorMsg(errorMsg.replace('Error: ', ''));
          }

          setData((prevState) => ({ ...prevState, email: currentEmail }));
        });
    };
    isValid();
  };

  return (
    <>
      <AccountForm
        title={formatAccountMessage({ id: 'personal.info.edit', defaultMessage: 'Edit personal information' })}
        requiredLabelIsVisible
        defaultCTASection
        loading={loading}
        disableSubmit={disableSubmit}
        formError={personalInfoSubmitErrorMsg}
        onSubmit={handleSubmit}
      >
        <div className="grid gap-12">
          {personalDetails.map((fieldProps, index) => {
            if (fieldProps.name == 'birthday') {
              if (!data?.birthday) {
                return <Input key={index} {...fieldProps} onChange={handleChange} value={'0000-00-00'} />;
              }
              return (
                <Input
                  key={index}
                  {...fieldProps}
                  onChange={handleChange}
                  value={new Date(data?.birthday || '').toISOString().split('T')[0] ?? ''}
                />
              );
            }
            if (fieldProps.name == 'title') {
              return (
                <Select
                  key="title-field"
                  {...fieldProps}
                  defaultValue={{ name: data?.title, value: data?.title } as Option}
                  onChange={selectHandleChage}
                />
              );
            }
            return (
              <Input
                key={index}
                {...fieldProps}
                onChange={handleChange}
                value={data?.[fieldProps.name as inputNameType] ?? ''}
                isValid={
                  (!fieldProps.required || (fieldProps.required && !!data[fieldProps.name as keyof Account])) &&
                  fieldProps.validation &&
                  fieldProps.validation(data[fieldProps.name as keyof Account] as string)
                }
              />
            );
          })}
        </div>
      </AccountForm>
      <AccountForm
        title={formatAccountMessage({ id: 'change.email.address', defaultMessage: 'Change email address' })}
        requiredLabelIsVisible
        defaultCTASection
        loading={emailLoading}
        disableSubmit={disableEmailSubmit}
        formError={emailSubmitErrorMsg}
        onSubmit={handleEmailSubmit}
      >
        <div className="grid gap-12">
          {emailField.map((fieldProps, index) => {
            return (
              <Input
                key={index}
                {...fieldProps}
                onChange={handleEmailChange}
                value={emailData?.[fieldProps.name as inputEmailType] ?? ''}
                isValid={
                  (!fieldProps.required || (fieldProps.required && !!emailData[fieldProps.name as keyof EmailProps])) &&
                  fieldProps.validation &&
                  fieldProps.validation(emailData[fieldProps.name as keyof EmailProps] as string)
                }
              />
            );
          })}
        </div>
      </AccountForm>
    </>
  );
};

export default PersonalInfoForm;
